import { cx } from './ui'

/**
 * Placeholder blocks for data that comes from the server and can take a
 * moment (the library and its analytics are online-only; everything synced
 * never shows one of these). `busy` dims content that is already there
 * while a fresh answer is on its way, so the page never blanks.
 */
export const Skeleton = ({ className }: { className?: string }) => (
  <div className={cx('animate-pulse rounded-[var(--radius-card)] bg-raised/70', className)} aria-hidden />
)

export const SkeletonInsights = () => (
  <div className="flex flex-col gap-5" data-testid="skeleton" aria-busy="true" aria-label="Loading">
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
      {Array.from({ length: 6 }, (_, i) => (
        <Skeleton key={i} className="h-[72px]" />
      ))}
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
      {Array.from({ length: 4 }, (_, i) => (
        <Skeleton key={i} className="h-[84px]" />
      ))}
    </div>
    <Skeleton className="h-48" />
    <div className="grid md:grid-cols-2 gap-4">
      <Skeleton className="h-64" />
      <Skeleton className="h-64" />
    </div>
  </div>
)

export const SkeletonList = ({ rows = 8 }: { rows?: number }) => (
  <div className="flex flex-col gap-1.5" data-testid="skeleton" aria-busy="true" aria-label="Loading">
    {Array.from({ length: rows }, (_, i) => (
      <Skeleton key={i} className="h-14" />
    ))}
  </div>
)

/** Wraps content that is being refreshed: a thin bar at the top and a dimmed body. */
export const Busy = ({ busy, children }: { busy: boolean; children: React.ReactNode }) => (
  <div className="relative">
    {busy && (
      <div className="absolute inset-x-0 -top-1 h-0.5 overflow-hidden rounded-full" aria-hidden>
        <div className="h-full w-1/3 bg-accent animate-[busy_1.1s_ease-in-out_infinite]" />
      </div>
    )}
    <div className={cx('transition-opacity duration-200', busy && 'opacity-60 pointer-events-none')}>
      {children}
    </div>
  </div>
)
